import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { questionnairesApi } from 'app/services/questionnaires';
import lodashSet from 'lodash.set';
import { resetQuestionnaire } from '../../actions';

export interface ProfitAndLossStatementStateValues {
  doYouHaveAProfitAndLossStatementPreparedForYear: Nullable<TYesOrNo>;
  pleaseUploadYourProfitAndLossStatements: Nullable<string>;
  revenue: Nullable<string>;
  returnsRefunds: Nullable<string>;
  accounting: Nullable<string>;
  advertising: Nullable<string>;
  bankCharges: Nullable<string>;
  commissions: Nullable<string>;
  comupterAndInternet: Nullable<string>;
  contractLabor: Nullable<string>;
  domainFees: Nullable<string>;
  duesAndSubscriptions: Nullable<string>;
  entertainment: Nullable<string>;
  healthInsurance: Nullable<string>;
  interest: Nullable<string>;
  liabilityInsurance: Nullable<string>;
  marketing: Nullable<string>;
  meals: Nullable<string>;
  officeExpense: Nullable<string>;
  outsideServices: Nullable<string>;
  parkingAndTolls: Nullable<string>;
  postageAndDelivery: Nullable<string>;
  printing: Nullable<string>;
  rent: Nullable<string>;
  repairsAndMaintenance: Nullable<string>;
  seminarsAndTraining: Nullable<string>;
  supplies: Nullable<string>;
  taxes: Nullable<string>;
  telephone: Nullable<string>;
  travel: Nullable<string>;
  utilities: Nullable<string>;
  wages: Nullable<string>;
  otherExpensesList: otherExpensesList;
}

export interface ProfitAndLossStatementState {
  values: ProfitAndLossStatementStateValues;
}

type otherExpensesList = otherExpensesListRow[];
export type otherExpensesListRow = {
  name: Nullable<string>;
  amount: Nullable<string>;
};
const initialState: ProfitAndLossStatementState = {
  values: {
    doYouHaveAProfitAndLossStatementPreparedForYear: null,
    pleaseUploadYourProfitAndLossStatements: null,
    revenue: null,
    returnsRefunds: null,
    accounting: null,
    advertising: null,
    bankCharges: null,
    commissions: null,
    comupterAndInternet: null,
    contractLabor: null,
    domainFees: null,
    duesAndSubscriptions: null,
    entertainment: null,
    healthInsurance: null,
    interest: null,
    liabilityInsurance: null,
    marketing: null,
    meals: null,
    officeExpense: null,
    outsideServices: null,
    parkingAndTolls: null,
    postageAndDelivery: null,
    printing: null,
    rent: null,
    repairsAndMaintenance: null,
    seminarsAndTraining: null,
    supplies: null,
    taxes: null,
    telephone: null,
    travel: null,
    utilities: null,
    wages: null,
    otherExpensesList: [],
  },
};

const ProfitAndLossStatementSlice = createSlice({
  name: 'ProfitAndLossStatement',
  initialState,
  reducers: {
    reset(state) {
      state = initialState;
    },
    update(state, action: PayloadAction<ProfitAndLossStatementStateValues>) {
      state.values = action.payload;
    },
    updateKey(
      state,
      action: PayloadAction<{
        key: keyof ProfitAndLossStatementStateValues;
        value: any;
      }>,
    ) {
      lodashSet(state, `values.${action.payload.key}`, action.payload.value);
    },

    // Update KEY_HERE_LIST if there is a Record Here
    updateFieldArrayKey(
      state: any,
      action: PayloadAction<{
        key: keyof ProfitAndLossStatementStateValues | '';
        value: any;
      }>,
    ) {
      const fieldArray =
        action.payload.value &&
        action.payload.value.map(({ ...field }) => ({
          ...field,
        }));
      if (action.payload.key === 'otherExpensesList') {
        state.values[action.payload.key] = fieldArray;
      } else {
        lodashSet(state, `values.${action.payload.key}`, fieldArray);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetQuestionnaire, () => initialState);

    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        state.values = payload.questionnaire.data.taxQuestionnaire
          ? payload.questionnaire.data.taxQuestionnaire?.additionalQuestions
              ?.profitandLossStatement?.values
          : initialState.values;
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey } =
  ProfitAndLossStatementSlice.actions;
export default ProfitAndLossStatementSlice.reducer;

export const selectProfitAndLossStatement = (state: RootState) =>
  state.taxQuestionnaire.additionalQuestions.profitandLossStatement;
